import { throwRepoErrorFromApiV3Error } from '@webapp/core/utils/repositories/throwRepoErrorFromApiV3Error'
import { type RepoStandardResponse } from '@webapp/infrastructure/interfaces/repositories/RepoStandardResponse'
import { type IHttpClient } from '@webapp/infrastructure/interfaces/services/IHttpClient'
import { type IFinancialAccountRepository } from '@webapp/platform/banking/business-logic/interfaces/IFinancialAccountRepository'
import { type FinancialAccountConstructor } from '@webapp/platform/banking/entities/FinancialAccount'
import { type FinancialAccountBalanceSummaryConstructor } from '@webapp/platform/banking/entities/FinancialAccountBalanceSummary/FinancialAccountBalanceSummary'
import { type FinancialAccountBankingDetailsConstructor } from '@webapp/platform/banking/entities/FinancialAccountBankingDetails/FinancialAccountBankingDetails'
import { mapFinancialAccountApiModel } from '../../mappers/mapFinancialAccountApiModel'
import { mapFinancialAccountBalanceSummaryApiModel } from '../../mappers/mapFinancialAccountBalanceSummaryApiModel'
import { mapFinancialAccountBankingDetailsApiModel } from '../../mappers/mapFinancialAccountBankingDetailsApiModel'
import { type GetFinancialAccountBalanceSummaryResponse } from '../../types/GetFinancialAccountBalanceSummaryResponse'
import { type GetFinancialAccountBankingDetailsResponse } from '../../types/GetFinancialAccountBankingDetailsResponse'
import { type GetFinancialAccountsResponse } from '../../types/GetFinancialAccountsResponse'
import { type GetScaAuthCodeResponse } from '../../types/GetScaAuthCodeResponse'
import { type GetTransfersAuthCodeResponse } from '../../types/GetTransfersAuthCodeResponse'

interface Dependency {
  httpClient: IHttpClient
}

export class FinancialAccountRepository implements IFinancialAccountRepository {
  constructor(private readonly deps: Dependency) {}

  async getFinancialAccounts(): Promise<
    RepoStandardResponse<FinancialAccountConstructor[]>
  > {
    try {
      const res = await this.deps.httpClient.get<GetFinancialAccountsResponse>(
        `/api/v3/payments/payment-sources/`
      )

      const financialAccounts = res.data.data || []

      return {
        data: financialAccounts.map((account) =>
          mapFinancialAccountApiModel(account)
        ),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getFinancialAccountBalanceSummary(
    financialAccountId: number,
    scaToken?: string
  ): Promise<RepoStandardResponse<FinancialAccountBalanceSummaryConstructor>> {
    try {
      const res =
        await this.deps.httpClient.get<GetFinancialAccountBalanceSummaryResponse>(
          `/api/v3/payments/payment-sources/${financialAccountId}/balance/`,
          { headers: scaToken ? { 'x-sca-token': scaToken } : undefined }
        )

      const balanceSummary = res.data.data

      return {
        data: mapFinancialAccountBalanceSummaryApiModel(balanceSummary),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getFinancialAccountBankingDetails(
    financialAccountId: number
  ): Promise<RepoStandardResponse<FinancialAccountBankingDetailsConstructor>> {
    try {
      const res =
        await this.deps.httpClient.get<GetFinancialAccountBankingDetailsResponse>(
          `/api/v3/payments/payment-sources/${financialAccountId}/fund-details/`
        )

      const details = res.data.data

      return {
        data: mapFinancialAccountBankingDetailsApiModel(details),
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getScaAuthCode(
    codeChallenge: string
  ): Promise<RepoStandardResponse<{ authCode: string }>> {
    try {
      const res = await this.deps.httpClient.post<
        GetScaAuthCodeResponse,
        { code_challenge: string }
      >(`/api/v3/pay/sca-auth-code/`, { code_challenge: codeChallenge })

      const data = res.data.data

      return {
        data: { authCode: data.authorization_code },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }

  async getAirwallexAuthCode(
    codeChallenge: string
  ): Promise<RepoStandardResponse<{ authCode: string }>> {
    try {
      const res = await this.deps.httpClient.post<
        GetTransfersAuthCodeResponse,
        { code_challenge: string }
      >(`/api/v3/payments/airwallex/auth-code/`, {
        code_challenge: codeChallenge,
      })

      const data = res.data.data

      return {
        data: { authCode: data.authorization_code },
      }
    } catch (err) {
      throw throwRepoErrorFromApiV3Error(err)
    }
  }
}
